import { FC, useCallback, useEffect, useState } from 'react';
import { Typography } from '@packages/ui/shared';
import { useTranslation } from 'react-i18next';
import { useDmsDispatch, useDmsState } from '@packages/contexts/dms';
import { useParams } from 'react-router-dom';
import { dmsService } from '@web/services/singletons';
import { Campaign } from '@packages/models/api';
import { TableRenderer } from '@web/components/table';
import { CampaignsTableHeader } from './campaigns-table-header';
import { createUseStyles } from 'react-jss';
import { CampaignsTableRow } from './campaigns-table-row';

export interface CampaignsTableRowModel extends Campaign {
    checked: boolean;
    rowId: string;
}

const CAMPAIGN_TITLE = 'Campaigns: Recalls & Services';
const EMPTY_STATE_DESC = 'No Recalls or Service Campaigns available for this vehicle.';

const useCampaignsListStyles = createUseStyles({
    tableContainer: {
        minHeight: '82vh',
    },
    outerTable: {
        height: '100%',
        minHeight: 'auto',
    },
});

export const CampaignsList: FC = () => {
    const { t } = useTranslation();
    const { vin: vinString } = useParams<{ vin: string }>();
    const classes = useCampaignsListStyles();
    const dispatch = useDmsDispatch();
    const { hasCampaignHistoryFetched, isCampaignLoading, campaignsByVin } = useDmsState();
    const [roTableRows, setRoTableRows] = useState<CampaignsTableRowModel[]>([]);

    useEffect(() => {
        if (vinString && !hasCampaignHistoryFetched) {
            dmsService.fetchAndSetCampaignsByVin(vinString, dispatch);
        }
    }, [vinString, hasCampaignHistoryFetched, dispatch]);

    useEffect(() => {
        if (hasCampaignHistoryFetched && campaignsByVin.length > 0) {
            const tableRows = campaignsByVin.map((campaign) => ({
                ...campaign,
                checked: false,
                rowId: campaign.code ?? Math.random().toString(),
            }));
            setRoTableRows(tableRows);
        } else {
            setRoTableRows([]);
        }
    }, [hasCampaignHistoryFetched, campaignsByVin]);

    const renderHeader = useCallback(() => <CampaignsTableHeader />, []);
    const renderRow = useCallback((rowData: CampaignsTableRowModel) => <CampaignsTableRow rowData={rowData} />, []);

    return (
        <div className={`pt-7 pl-7 pr-7 d-flex ${!campaignsByVin?.length && classes.tableContainer}`}>
            <div className="d-flex w-100 flex-column">
                <header className="mb-6 d-flex align-items-center">
                    <Typography variant="h2">{t(CAMPAIGN_TITLE)}</Typography>
                </header>

                <TableRenderer<CampaignsTableRowModel>
                    emptyStateProps={{
                        icon: 'recall-swap-driving',
                        description: t(EMPTY_STATE_DESC),
                    }}
                    isLoading={isCampaignLoading}
                    useFixedLayout
                    outerTableClassName={classes.outerTable}
                    tableBodyRowRenderer={renderRow}
                    tableHeaderRowRenderer={renderHeader}
                    tableRowsData={roTableRows}
                    tableRowsDataSetter={setRoTableRows}
                />
            </div>
        </div>
    );
};
